import { useState, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT } from '../legacy-ball/table-management/designTokens';

interface Contributor {
  name: string;
  tilePurchase: boolean;
  article: boolean;
  adPurchase: boolean;
  reviewer: boolean;
}

interface ContributorsTableViewProps {
  contributors: Contributor[];
}

type CategoryFilter = 'all' | 'tilePurchase' | 'article' | 'adPurchase' | 'reviewer';

const FILTER_OPTIONS: { key: CategoryFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'tilePurchase', label: 'Tile Purchase' },
  { key: 'article', label: 'Article' },
  { key: 'adPurchase', label: 'Ad Purchase' },
  { key: 'reviewer', label: 'Reviewer' },
];

export function ContributorsTableView({ contributors }: ContributorsTableViewProps) {
  const [filter, setFilter] = useState<CategoryFilter>('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return contributors;
    return contributors.filter((c) => c[filter]);
  }, [contributors, filter]);

  // Compute totals from the filtered list
  const totals = useMemo(() => {
    let tilePurchase = 0;
    let article = 0;
    let adPurchase = 0;
    let reviewer = 0;
    for (const c of filtered) {
      if (c.tilePurchase) tilePurchase++;
      if (c.article) article++;
      if (c.adPurchase) adPurchase++;
      if (c.reviewer) reviewer++;
    }
    return { tilePurchase, article, adPurchase, reviewer };
  }, [filtered]);

  return (
    <div>
      {/* Category filter pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => setFilter(opt.key)}
            style={{
              padding: '4px 10px',
              fontSize: '0.8rem',
              borderRadius: 14,
              border: filter === opt.key ? `1px solid ${PRIMARY}` : `1px solid ${BORDER_LIGHT}`,
              backgroundColor: filter === opt.key ? '#e8f5e9' : '#fff',
              color: filter === opt.key ? PRIMARY : TEXT_MUTED,
              fontWeight: filter === opt.key ? 600 : 400,
              cursor: 'pointer',
            }}
            aria-pressed={filter === opt.key}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem',
        }}
      >
        <thead>
          <tr>
            <th style={headerCellStyle}>Name</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>Tile Purchase</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>Article</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>Ad Purchase</th>
            <th style={{ ...headerCellStyle, textAlign: 'center' }}>Reviewer</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((contributor) => (
            <tr key={contributor.name}>
              <td style={cellStyle}>{contributor.name}</td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>
                <input type="checkbox" checked={contributor.tilePurchase} disabled />
              </td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>
                <input type="checkbox" checked={contributor.article} disabled />
              </td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>
                <input type="checkbox" checked={contributor.adPurchase} disabled />
              </td>
              <td style={{ ...cellStyle, textAlign: 'center' }}>
                <input type="checkbox" checked={contributor.reviewer} disabled />
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={totalsCellStyle}>Totals ({filtered.length})</td>
            <td style={{ ...totalsCellStyle, textAlign: 'center' }}>{totals.tilePurchase}</td>
            <td style={{ ...totalsCellStyle, textAlign: 'center' }}>{totals.article}</td>
            <td style={{ ...totalsCellStyle, textAlign: 'center' }}>{totals.adPurchase}</td>
            <td style={{ ...totalsCellStyle, textAlign: 'center' }}>{totals.reviewer}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const headerCellStyle: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: `2px solid ${BORDER_LIGHT}`,
  color: TEXT_DARK,
  fontWeight: 600,
  fontSize: '0.85rem',
};

const cellStyle: CSSProperties = {
  padding: '6px 10px',
  borderBottom: `1px solid ${BORDER_LIGHT}`,
  color: TEXT_MUTED,
};

const totalsCellStyle: CSSProperties = {
  padding: '8px 10px',
  borderTop: `2px solid ${BORDER_LIGHT}`,
  color: TEXT_DARK,
  fontWeight: 700,
  fontSize: '0.85rem',
};
